import { useEffect, useRef, useState } from 'react';

/**
 * Reports once an element has scrolled into view, then stops watching it —
 * what the landing page's reveal-on-scroll and stat counters hang off.
 *
 * Browsers without IntersectionObserver get `true` straight away, so the
 * content is never left invisible.
 *
 * @param {{threshold?: number, rootMargin?: string}} [options]
 * @returns {[React.RefObject<HTMLElement>, boolean]} a ref to attach, and whether it has been seen
 */
export function useInView({ threshold = 0.15, rootMargin = '0px' } = {}) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || seen) return undefined;
    if (!('IntersectionObserver' in window)) {
      setSeen(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setSeen(true);
        observer.disconnect();
      }
    }, { threshold, rootMargin });
    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin, seen]);

  return [ref, seen];
}

export default useInView;
